import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { COOKIE } from "@/constants/testIds";

const STORAGE_KEY = "ys_cookie_prefs";

export function getConsent() {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    } catch {
        return null;
    }
}

export const CookieBanner = () => {
    const [prefs, setPrefs] = useState(null);
    const [manageOpen, setManageOpen] = useState(false);
    const [analytics, setAnalytics] = useState(false);
    const [marketing, setMarketing] = useState(false);

    useEffect(() => {
        setPrefs(getConsent());
    }, []);

    if (prefs) return null;

    const save = (data) => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...data, ts: Date.now() }));
        setPrefs(data);
    };

    return (
        <>
            <div
                data-testid={COOKIE.banner}
                className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-md z-50 rounded-2xl bg-white border border-[#17252D]/10 shadow-[0_10px_30px_rgba(23,37,45,0.15)] p-4"
                style={{ marginBottom: "calc(env(safe-area-inset-bottom, 0px) + 64px)" }}
            >
                <div className="text-sm font-semibold text-[#17252D]">Cookies on YS Removals</div>
                <p className="mt-1 text-xs text-[#17252D]/75 leading-relaxed">
                    We use essential cookies to run the site. With your permission we’d also use analytics and marketing cookies to improve our services. You can change your choices at any time.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                        data-testid={COOKIE.accept}
                        onClick={() => save({ essential: true, analytics: true, marketing: true })}
                        className="bg-[#001B3D] hover:bg-[#001B3D]/90 text-white h-9"
                    >
                        Accept all
                    </Button>
                    <Button
                        data-testid={COOKIE.reject}
                        variant="outline"
                        onClick={() => save({ essential: true, analytics: false, marketing: false })}
                        className="h-9"
                    >
                        Reject non-essential
                    </Button>
                    <Sheet open={manageOpen} onOpenChange={setManageOpen}>
                        <SheetTrigger asChild>
                            <Button data-testid={COOKIE.manage} variant="ghost" className="h-9 text-[#001B3D]">Manage preferences</Button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-full sm:max-w-md">
                            <SheetHeader>
                                <SheetTitle>Cookie preferences</SheetTitle>
                                <SheetDescription>Choose which categories of cookies to allow.</SheetDescription>
                            </SheetHeader>
                            <div className="mt-6 space-y-4">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <Label className="font-semibold">Essential</Label>
                                        <p className="text-xs text-[#17252D]/70 mt-1">Required for the site to function. Always on.</p>
                                    </div>
                                    <Switch checked disabled />
                                </div>
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <Label htmlFor="c-analytics" className="font-semibold">Analytics</Label>
                                        <p className="text-xs text-[#17252D]/70 mt-1">Helps us understand how the site is used.</p>
                                    </div>
                                    <Switch id="c-analytics" checked={analytics} onCheckedChange={setAnalytics} />
                                </div>
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <Label htmlFor="c-marketing" className="font-semibold">Marketing</Label>
                                        <p className="text-xs text-[#17252D]/70 mt-1">Used to measure ad effectiveness.</p>
                                    </div>
                                    <Switch id="c-marketing" checked={marketing} onCheckedChange={setMarketing} />
                                </div>
                            </div>
                            <SheetFooter className="mt-8">
                                <Button
                                    onClick={() => {
                                        save({ essential: true, analytics, marketing });
                                        setManageOpen(false);
                                    }}
                                    className="bg-[#001B3D] hover:bg-[#001B3D]/90 text-white"
                                >
                                    Save preferences
                                </Button>
                            </SheetFooter>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </>
    );
};
